import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-[#F5F4F0] placeholder:text-[#525252] focus:outline-none focus:border-[#C9A26B] transition-colors duration-300 text-sm";

export default function ResetPassword() {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset", { token, password });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 py-24">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
        data-testid="reset-card"
      >
        <img src="/photos/logo-trimmed.webp" alt="TapeShift Weddings" className="h-10 w-auto mb-10" />
        <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B] mb-4">Password Reset</p>
        {done ? (
          <div data-testid="reset-success">
            <h1 className="font-serif font-light text-4xl tracking-tighter text-[#F5F4F0] mb-6">All <span className="font-accent italic text-[#C9A26B]">set.</span></h1>
            <p className="text-sm text-[#A3A3A3] leading-relaxed mb-10">Your password is updated. You can sign in with it now.</p>
            <a href="/gallery" data-testid="reset-signin-link" className="text-xs tracking-[0.35em] uppercase bg-[#C9A26B] text-[#0A0A0A] px-10 py-4 hover:bg-transparent hover:text-[#C9A26B] border border-[#C9A26B] transition-colors duration-400 inline-block">
              Sign In
            </a>
          </div>
        ) : (
          <>
            <h1 className="font-serif font-light text-4xl tracking-tighter text-[#F5F4F0] mb-10">Choose a new <span className="font-accent italic text-[#C9A26B]">password</span></h1>
            <form onSubmit={submit} className="flex flex-col gap-8" data-testid="reset-form">
              <div>
                <label htmlFor="reset-pass" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">New password</label>
                <input id="reset-pass" type="password" required minLength={6} data-testid="reset-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 6 characters" className={inputCls} />
              </div>
              <div>
                <label htmlFor="reset-confirm" className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Repeat password</label>
                <input id="reset-confirm" type="password" required minLength={6} data-testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Same again" className={inputCls} />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}
              <button type="submit" disabled={busy} data-testid="reset-submit-btn"
                className="self-start text-xs tracking-[0.35em] uppercase bg-[#C9A26B] text-[#0A0A0A] px-10 py-4 hover:bg-transparent hover:text-[#C9A26B] border border-[#C9A26B] transition-colors duration-400 disabled:opacity-50">
                {busy ? "Updating…" : "Update Password"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </main>
  );
}
